const SWIPE_THRESHOLD = 10;

export interface SwipeActionDef {
    label: string;
    className: string;
    onAction: () => void;
}

export interface SwipeRowActions {
    /** Pantry: left swipe past halfway auto-fires edit */
    onEdit?: () => void;
    /** Left swipe auto-fires on release past halfway (e.g. STOCK, TOSS) */
    left?: SwipeActionDef;
    /** Right swipe auto-fires on release past halfway (e.g. BUY) */
    right?: SwipeActionDef;
}

function measureButtonWidth(actionsEl: HTMLElement): number {
    const btn = actionsEl.querySelector<HTMLElement>(".swipe-action");
    return btn?.offsetWidth ?? 0;
}

export function createSwipeRow(content: HTMLElement, actions: SwipeRowActions): HTMLElement {
    const row = document.createElement("div");
    row.className = "swipe-row";

    const leftActionsEl = document.createElement("div");
    leftActionsEl.className = "swipe-row-actions swipe-row-actions--leading";

    if (actions.right) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `swipe-action ${actions.right.className}`;
        btn.textContent = actions.right.label;
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            closeRow();
            actions.right!.onAction();
        });
        leftActionsEl.appendChild(btn);
    }

    const rightActionsEl = document.createElement("div");
    rightActionsEl.className = "swipe-row-actions swipe-row-actions--trailing";

    if (actions.onEdit) {
        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "swipe-action swipe-action-edit";
        editBtn.textContent = "Edit";
        editBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            closeRow();
            actions.onEdit!();
        });
        rightActionsEl.appendChild(editBtn);
    }

    if (actions.left) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `swipe-action ${actions.left.className}`;
        btn.textContent = actions.left.label;
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            closeRow();
            actions.left!.onAction();
        });
        rightActionsEl.appendChild(btn);
    }

    const contentWrap = document.createElement("div");
    contentWrap.className = "swipe-row-content";
    if (actions.left || actions.onEdit) {
        contentWrap.classList.add("swipe-row-content--trailing-action");
    }
    if (actions.right) {
        contentWrap.classList.add("swipe-row-content--leading-action");
    }
    contentWrap.appendChild(content);

    row.appendChild(leftActionsEl);
    row.appendChild(rightActionsEl);
    row.appendChild(contentWrap);

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let dragging = false;
    let horizontalLock: boolean | null = null;
    let trailingOpen = 0;
    let leadingOpen = 0;

    function refreshOpenWidths(): void {
        trailingOpen = measureButtonWidth(rightActionsEl);
        leadingOpen = measureButtonWidth(leftActionsEl);
    }

    function setOffset(px: number): void {
        const minX = trailingOpen > 0 ? -trailingOpen : 0;
        const maxX = leadingOpen > 0 ? leadingOpen : 0;
        const clamped = Math.max(minX, Math.min(maxX, px));
        currentX = clamped;
        contentWrap.style.transform = `translateX(${clamped}px)`;
    }

    function closeRow(): void {
        setOffset(0);
    }

    function onPointerDown(e: PointerEvent): void {
        if ((e.target as HTMLElement).closest(".tally-btn, .tally-count, .zone-action-btn")) return;
        refreshOpenWidths();
        startX = e.clientX;
        startY = e.clientY;
        dragging = true;
        horizontalLock = null;
        contentWrap.setPointerCapture(e.pointerId);
        contentWrap.classList.add("is-dragging");
    }

    function onPointerMove(e: PointerEvent): void {
        if (!dragging) return;
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        if (horizontalLock === null) {
            if (Math.abs(deltaX) > SWIPE_THRESHOLD || Math.abs(deltaY) > SWIPE_THRESHOLD) {
                horizontalLock = Math.abs(deltaX) > Math.abs(deltaY);
            }
        }

        if (!horizontalLock) return;

        e.preventDefault();
        setOffset(deltaX);
    }

    function onPointerUp(e: PointerEvent): void {
        if (!dragging) return;
        dragging = false;
        contentWrap.classList.remove("is-dragging");
        contentWrap.releasePointerCapture(e.pointerId);

        if (horizontalLock) {
            if (currentX < 0 && trailingOpen > 0 && Math.abs(currentX) >= trailingOpen / 2) {
                closeRow();
                if (actions.onEdit) {
                    actions.onEdit();
                } else if (actions.left) {
                    actions.left.onAction();
                }
            } else if (currentX > 0 && leadingOpen > 0 && currentX >= leadingOpen / 2) {
                closeRow();
                if (actions.right) {
                    actions.right.onAction();
                }
            } else {
                closeRow();
            }
        }
        horizontalLock = null;
    }

    contentWrap.addEventListener("pointerdown", onPointerDown);
    contentWrap.addEventListener("pointermove", onPointerMove);
    contentWrap.addEventListener("pointerup", onPointerUp);
    contentWrap.addEventListener("pointercancel", onPointerUp);

    return row;
}
